import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Grid, Paper, Button, Divider, Chip } from '@mui/material';
import { ArrowRight, AlertCircle, Calendar, TrendingUp } from 'lucide-react';
import {} from '@diggens/shared-hooks';
import { useCurrentUser } from '@diggens/user-management';
import { portfolioMockData, dealFlowMockData, fundraisingMockData, irMockData } from '../components/dashboard/mockDashboardData';

export function Home() {
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();

  // Get enabled modules
  const enabledModules = useMemo(() => {
    if (!user?.tenants?.module_config) return [];
    const moduleConfig = user.tenants.module_config as Record<string, { enabled: boolean }>;
    return Object.entries(moduleConfig)
      .filter(([, config]) => config.enabled)
      .map(([id]) => id)
      .filter((id) => id !== 'pmn');
  }, [user]);

  const hasPortfolio = enabledModules.includes('pevc-portfolio');
  const hasDealFlow = enabledModules.includes('pevc-dealflow');
  const hasFundraising = enabledModules.includes('pevc-fundraising');
  const hasIR = enabledModules.includes('pevc-ir');

  // Alerts requiring MD attention
  const alerts = useMemo(() => {
    const allAlerts = [];
    if (hasPortfolio && portfolioMockData.Alerts?.items) {
      allAlerts.push(...portfolioMockData.Alerts.items.slice(0, 3));
    }
    if (hasDealFlow && dealFlowMockData['Stale Deals List']?.items) {
      allAlerts.push(...dealFlowMockData['Stale Deals List'].items.slice(0, 2));
    }
    return allAlerts.slice(0, 5);
  }, [hasPortfolio, hasDealFlow]);

  // Upcoming meetings
  const upcomingMeetings = useMemo(() => {
    const meetings = [];
    if (hasPortfolio && portfolioMockData['Upcoming Board Meetings']?.items) {
      meetings.push(...portfolioMockData['Upcoming Board Meetings'].items.slice(0, 2));
    }
    if (hasDealFlow && dealFlowMockData['IC Calendar']?.items) {
      meetings.push(...dealFlowMockData['IC Calendar'].items.slice(0, 2));
    }
    if (hasFundraising && fundraisingMockData['Upcoming Meetings']?.items) {
      meetings.push(...fundraisingMockData['Upcoming Meetings'].items.slice(0, 1));
    }
    return meetings.slice(0, 5);
  }, [hasPortfolio, hasDealFlow, hasFundraising]);

  // Team activity
  const recentActivities = useMemo(() => {
    const activities = [];
    if (hasPortfolio && portfolioMockData['Recent Submissions']?.activities) {
      activities.push(...portfolioMockData['Recent Submissions'].activities.slice(0, 2));
    }
    if (hasDealFlow && dealFlowMockData['Recent Activity']?.activities) {
      activities.push(...dealFlowMockData['Recent Activity'].activities.slice(0, 3));
    }
    return activities.slice(0, 5);
  }, [hasPortfolio, hasDealFlow]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Executive Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" fontWeight={700}>
          Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {user?.full_name?.split(' ')[0]}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
          {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Main Content */}
        <Grid size={{ xs: 12, lg: 8 }}>
          {/* Fund Performance Summary */}
          {hasPortfolio && (
            <Paper
              sx={{
                p: 3,
                mb: 3,
                background: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(59,130,246,0.04) 100%)'
                    : 'linear-gradient(135deg, rgba(16,185,129,0.04) 0%, rgba(59,130,246,0.02) 100%)',
                borderLeft: '4px solid',
                borderColor: 'success.main',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    Fund Performance
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    As of today
                  </Typography>
                </Box>
                <Button size="small" endIcon={<ArrowRight size={16} />} onClick={() => navigate('/portfolio/dashboard')}>
                  Full Report
                </Button>
              </Box>

              <Grid container spacing={3}>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={600} letterSpacing={1}>
                    TVPI
                  </Typography>
                  <Typography variant="h2" fontWeight={700} sx={{ my: 0.5 }}>
                    {portfolioMockData.TVPI?.value}x
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Value / Paid-In
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={600} letterSpacing={1}>
                    Net IRR
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, my: 0.5 }}>
                    <Typography variant="h2" fontWeight={700} color="success.main">
                      {portfolioMockData['Net IRR']?.value}%
                    </Typography>
                    <TrendingUp size={24} color="#10b981" />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Since inception
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={600} letterSpacing={1}>
                    Portfolio
                  </Typography>
                  <Typography variant="h2" fontWeight={700} sx={{ my: 0.5 }}>
                    {portfolioMockData['Active Companies']?.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Active companies
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={600} letterSpacing={1}>
                    NAV
                  </Typography>
                  <Typography variant="h2" fontWeight={700} sx={{ my: 0.5 }}>
                    ${(Number(portfolioMockData['Current NAV']?.value ?? 0) / 1000000).toFixed(0)}M
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    +{portfolioMockData['Current NAV']?.trend}% QoQ
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Key Metrics Row */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {hasDealFlow && (
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => navigate('/dealflow/dashboard')}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={600}>
                      Deal Pipeline
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ my: 1 }}>
                      {dealFlowMockData['Active Deals']?.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ${(Number(dealFlowMockData['Pipeline Value']?.value ?? 0) / 1000000).toFixed(0)}M total value
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {hasFundraising && (
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => navigate('/fundraising/dashboard')}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={600}>
                      Fundraising
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ my: 1 }}>
                      {fundraisingMockData['% to Target']?.value}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ${(Number(fundraisingMockData['Total Committed']?.value ?? 0) / 1000000).toFixed(0)}M committed
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {hasIR && (
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => navigate('/ir/dashboard')}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={600}>
                      LP Relations
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ my: 1 }}>
                      {irMockData['Total LPs']?.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {irMockData['Upcoming Meetings']?.value} meetings scheduled
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {/* Team Activity */}
          {recentActivities.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Team Activity
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {recentActivities.map((activity, idx) => (
                  <Box key={activity.id}>
                    <Typography variant="body2" fontWeight={500}>
                      {activity.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {activity.entity} • {activity.timestamp}
                    </Typography>
                    {idx < recentActivities.length - 1 && <Divider sx={{ mt: 2 }} />}
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
        </Grid>

        {/* Right Column - Sidebar */}
        <Grid size={{ xs: 12, lg: 4 }}>
          {/* Items Requiring Attention */}
          {alerts.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AlertCircle size={20} color="#f59e0b" />
                <Typography variant="h6" fontWeight={600}>
                  Requires Attention
                </Typography>
                <Chip label={alerts.length} size="small" color="warning" />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {alerts.map((alert, idx) => (
                  <Box key={alert.id}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1 }}>
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: alert.status === 'overdue' ? 'error.main' : 'warning.main',
                          mt: 0.75,
                          flexShrink: 0,
                        }}
                      />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {alert.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {alert.subtitle}
                        </Typography>
                      </Box>
                    </Box>
                    {idx < alerts.length - 1 && <Divider />}
                  </Box>
                ))}
              </Box>
            </Paper>
          )}

          {/* Upcoming Calendar */}
          {upcomingMeetings.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Calendar size={20} />
                <Typography variant="h6" fontWeight={600}>
                  Your Calendar
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {upcomingMeetings.map((meeting, idx) => (
                  <Box key={meeting.id}>
                    <Typography variant="body2" fontWeight={500}>
                      {meeting.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {meeting.date}
                    </Typography>
                    {idx < upcomingMeetings.length - 1 && <Divider sx={{ mt: 2 }} />}
                  </Box>
                ))}
              </Box>
            </Paper>
          )}

          {/* Quick Access */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Quick Access
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {hasPortfolio && (
                <Button variant="text" endIcon={<ArrowRight size={16} />} onClick={() => navigate('/portfolio/dashboard')} sx={{ justifyContent: 'space-between', textAlign: 'left' }}>
                  Portfolio Dashboard
                </Button>
              )}
              {hasDealFlow && (
                <Button variant="text" endIcon={<ArrowRight size={16} />} onClick={() => navigate('/dealflow/dashboard')} sx={{ justifyContent: 'space-between', textAlign: 'left' }}>
                  Deal Pipeline
                </Button>
              )}
              {hasFundraising && (
                <Button variant="text" endIcon={<ArrowRight size={16} />} onClick={() => navigate('/fundraising/dashboard')} sx={{ justifyContent: 'space-between', textAlign: 'left' }}>
                  Fundraising
                </Button>
              )}
              <Button variant="text" endIcon={<ArrowRight size={16} />} onClick={() => navigate('/analytics')} sx={{ justifyContent: 'space-between', textAlign: 'left' }}>
                Analytics
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
